/**
 * THE INDICATOR LINES' OWN DEFAULT COLOURS, in one declaration.
 *
 * <h2>Why these are not simply inside the chart's theme objects</h2>
 *
 * They are — the theme objects read them from here. The problem is that TWO places need the
 * default: the series that draws the line, and the configuration dialog's swatch, which has to
 * open on the colour the line currently is. The dialog is handed its defaults by `App`, so
 * without a shared declaration the value exists twice and the swatch can quietly show a colour
 * the chart is not using.
 *
 * <p>That is not hypothetical. `rsiLine` was added as a workspace colour role and to both chart
 * themes, and NOT to `App`'s defaults map — so the dialog fell through to its own `'#888888'`
 * placeholder and offered a grey swatch for a line that was drawing blue. A reader opening that
 * picker is being shown the wrong current colour before they touch anything.
 *
 * <h2>Its own module rather than an export from `EChartComponent`</h2>
 *
 * Importing that file into a test pulls echarts' ESM entry through jest, which does not transform
 * it, and the suite fails to parse before a single assertion runs. A table of two hex strings
 * should not need a chart library mocked to be read.
 */

/** Light panels. The blue the RSI series hardcoded before the role existed. */
export const RSI_LINE_LIGHT = '#1976d2';

/**
 * Dark panels. Lighter, same hue.
 *
 * <p>#1976d2 on a dark panel is close to unreadable and the RSI line is thin, so one stored hex
 * cannot serve both themes — which is also why the ROLE falls back to the active theme's value
 * rather than to a single constant. A reader who has expressed no preference must not be handed
 * one that is wrong on their theme.
 */
export const RSI_LINE_DARK = '#64b5f6';

/**
 * The RSI line's default for a theme mode.
 *
 * @param {string} mode MUI's `palette.mode` — anything that is not 'dark' is treated as light,
 *                      because a missing mode should render the ordinary chart rather than an
 *                      unreadable one.
 */
export const rsiLineDefault = (mode) => (mode === 'dark' ? RSI_LINE_DARK : RSI_LINE_LIGHT);
